import { createSlice, createAsyncThunk, isRejected } from "@reduxjs/toolkit";
import axios from "axios";
import { nanoid } from "@reduxjs/toolkit";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Utility for consistent error extraction
const handleError = (err, rejectWithValue) => {
  if (err.response && err.response.data) {
    return rejectWithValue(err.response.data.status || err.response.data);
  }
  return rejectWithValue(err.message || "Something went wrong");
};

// Save normal ruleset
export const saveNormalRuleset = createAsyncThunk(
  "ruleFrame/saveNormalRuleset",
  async ({ subtests, options, license_key, category }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/rulesets/normal_save/`,
        { license_key, category, subtests, options },
      );
      return res.data;
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Fetch categories
export const fetchCategories = createAsyncThunk(
  "ruleFrame/fetchCategories",
  async (license_key, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/`,
        {
          license_key,
        },
      );
      return res.data || [];
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Fetch subtests for a category
export const fetchCategorySubtests = createAsyncThunk(
  "ruleFrame/fetchCategorySubtests",
  async ({ categoryName, license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/subtests/`,
        { license_key, categoryName },
      );
      return res.data || { category: null, subtests: [] };
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Add new category
export const addCategory = createAsyncThunk(
  "ruleFrame/addCategory",
  async ({ category, license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/add/`,
        { category, license_key },
      );
      return res.data || {};
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Reset category data
export const resetCategoryData = createAsyncThunk(
  "ruleFrame/resetCategoryData",
  async ({ license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/reset/`,
        { license_key },
      );
      return res.data || {};
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Edit category
export const editCategory = createAsyncThunk(
  "ruleFrame/editCategory",
  async ({ oldName, newName, license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/edit/`,
        { oldName, newName, license_key },
      );
      return res.data || {};
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

// Delete category
export const deleteCategory = createAsyncThunk(
  "ruleFrame/deleteCategory",
  async ({ category, license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/categories/delete/`,
        { category, license_key },
      );
      return res.data || {};
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  },
);

const ruleFrameSlice = createSlice({
  name: "ruleFrame",
  initialState: {
    categories: [],
    selectedCategory: null,
    categoryType: {},
    category_data: null,
    subtests: [],
    loading: false,
    ruleloading: false,
    error: null,
    ruleerror: null,
    rulesuccess: null,
    deploymentType: "default",
    options: {
      skipWithoutRanges: false,
      fileNormal: false,
      keywordRule: 0,
      keywords: "",
      fileMethod: 0,
      fileComment: "",
      withoutComment: "** Normal",
    },
  },
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setOption: (state, action) => {
      const { key, value } = action.payload;
      state.options[key] = value;
    },
    toggleCategoryType: (state, action) => {
      const cat = action.payload;
      if (state.categoryType[cat] !== undefined) {
        state.categoryType[cat] = !state.categoryType[cat];
      }
    },
    updateCategoryData: (state, action) => {
      state.category_data = { ...state.category_data, ...action.payload };
    },
    updateSubtest: (state, action) => {
      const { id, changes } = action.payload;
      const idx = state.subtests.findIndex((s) => s.id === id);
      if (idx !== -1) {
        state.subtests[idx] = { ...state.subtests[idx], ...changes };
      }
    },
    addSubtest: (state, action) => {
      const newSubtest = {
        id: Date.now(),
        ...action.payload,
        reviewed: true,
        min_val: null,
        max_val: null,
        lab_min_val: null,
        lab_max_val: null,
        previous_type: 0,
        days_or_tests: null,
        pdiv_below: null,
        pdiv_above: null,
        details: null,
      };
      state.subtests.push(newSubtest);
    },
    deleteSubtest: (state, action) => {
      state.subtests = state.subtests.filter((s) => s.id !== action.payload);
    },

    splitSubtest: (state, action) => {
      const subtestId = action.payload;
      const target = state.subtests.find((s) => s.id === subtestId);
      if (!target) return;

      const exists = (gender) =>
        state.subtests.some(
          (s) =>
            s.id !== subtestId &&
            s.sub_test_name === target.sub_test_name &&
            s.gender === gender &&
            s.min_age === target.min_age &&
            s.max_age === target.max_age,
        );

      state.subtests = state.subtests.filter((s) => s.id !== subtestId);

      const makeCopy = (gender) => ({
        ...target,
        id: nanoid(),
        gender,
      });

      if (!target.gender || target.gender === "Both") {
        if (!exists("Male")) state.subtests.push(makeCopy("Male"));
        if (!exists("Female")) state.subtests.push(makeCopy("Female"));
      } else {
        state.subtests.push(target);
        const other = target.gender === "Male" ? "Female" : "Male";
        if (!exists(other)) state.subtests.push(makeCopy(other));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Normal ruleset
      .addCase(saveNormalRuleset.pending, (state) => {
        state.ruleloading = true;
        state.ruleerror = null;
        state.rulesuccess = null;
      })
      .addCase(saveNormalRuleset.fulfilled, (state, action) => {
        state.ruleloading = false;
        state.rulesuccess =
          action.payload?.message || "Ruleset saved successfully";
      })
      .addCase(saveNormalRuleset.rejected, (state, action) => {
        state.ruleloading = false;
        state.ruleerror = action.payload || action.error.message;
      })

      // Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.ruleerror = null;
        state.rulesuccess = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { category_names, category_type } = action.payload;
        state.categories = category_names;
        state.categoryType = category_type;
        if (!state.selectedCategory && category_names.length > 0) {
          state.selectedCategory = category_names[0];
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Category subtests
      .addCase(fetchCategorySubtests.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.ruleerror = null;
        state.rulesuccess = null;
      })
      .addCase(fetchCategorySubtests.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { category, subtests } = action.payload;
        state.category_data = category;
        state.subtests = subtests || [];
      })
      .addCase(fetchCategorySubtests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Add category
      .addCase(addCategory.fulfilled, (state, action) => {
        const newCat = action.payload.category;
        state.categories.push(newCat);
        const isBasicMode =
          state.selectedCategory &&
          state.categoryType[state.selectedCategory] === false;
        state.selectedCategory = newCat;
        state.categoryType[newCat] = isBasicMode;
      })

      // Reset
      .addCase(resetCategoryData.fulfilled, (state) => {
        state.error = null;
      })

      // Edit
      .addCase(editCategory.fulfilled, (state, action) => {
        const idx = state.categories.findIndex(
          (c) => c === action.payload.oldName,
        );
        if (idx > -1) {
          state.categories[idx] = action.payload.newName;
          state.selectedCategory = state.categories[idx] || null;
        }
      })

      // Delete
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (c) => c !== action.payload.category,
        );
        if (state.selectedCategory === action.payload.category) {
          state.selectedCategory = state.categories[0] || null;
        }
      })

      // Global rejected handler
      .addMatcher(isRejected, (state, action) => {
        if (action.type.startsWith("ruleFrame/")) {
          state.error = action.payload || action.error.message;
        }
      });
  },
});

export const {
  setSelectedCategory,
  setOption,
  toggleCategoryType,
  updateCategoryData,
  updateSubtest,
  addSubtest,
  deleteSubtest,
  splitSubtest,
} = ruleFrameSlice.actions;

export default ruleFrameSlice.reducer;
